import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import gsap from 'gsap'
import { Link } from 'react-router-dom'
import Logo from '../assets/logo-3.3.png'

function Menu() {
    const [toggle, setToggle] = useState(false)
    const [animate, setAnimate] = useState(true)
    const wrapper = useRef(null)
    const links = useRef(null)
    const navbar = useRef(null)
    const footer = useRef(null)

    const toggleAnimation = (state) => {
        const tl = gsap.timeline()
        if (state === "entrance" && animate) {
            setAnimate(false)

            // WRAPPER ANIMATION
            tl.fromTo(wrapper.current, { opacity: 0, }, { opacity: 1, display: 'flex' })

            // LINK ANIMATION
            tl.fromTo(links.current.children, { opacity: 0, y: -50, }, {
                opacity: 1,
                y: 0,
                stagger: {
                    from: "start",
                    each: 0.2
                },
                onComplete: () => setAnimate(true)
            }, '>1')

            // NAVBAR ANIMATION
            tl.fromTo([navbar.current, footer.current], { opacity: 0 }, { opacity: 1 }, '>0.5')

        } else if (state === "exit" && animate) {
            // EXIT ANIMATION
            setAnimate(false)

            // LINK ANIMATION
            tl.to(links.current.children, {
                opacity: 0,
                y: -50,
                stagger: {
                    from: "start",
                    each: 0.2
                }
            })

            // NAVBAR ANIMATION
            tl.fromTo(footer.current, { opacity: 1 }, { opacity: 0 })
            tl.fromTo(navbar.current, { opacity: 1 }, { opacity: 0 })

            // WRAPPER ANIMATION
            tl.fromTo(wrapper.current, {
                opacity: 1,
            }, {
                opacity: 0,
                display: 'none',
                onComplete: () => {
                    setAnimate(true)
                }
            }, '<0.5')

            tl.fromTo(navbar.current, { opacity: 0 }, { opacity: 1 }, '<1')
        }
    }

    const toggleNav = (toggle) => {
        if (toggle) {
            toggleAnimation("entrance")

            setToggle(true)
        } else {
            toggleAnimation("exit")

            setToggle(false)
        }
    }

    useEffect(() => {
        // menu toggle off when page opened
        gsap.set(wrapper.current, {
            opacity: 0,
            display: 'none'
        })
        setToggle(false)
    }, [])

    return (
        <>
            {/* Navbar */}
            <nav className="navbar" ref={navbar}>
                {/* icon toggle */}
                <div className="navbar-header">
                    <div className="logo">
                        <img src={Logo} alt="" />
                    </div>
                    {/* change color on menu toggle state */}
                    <Link to="/">
                        <h1 className={`${toggle ? 'menu-header' : ''}`}>
                            {toggle ? 'HOME' : 'NOXTURNE'}
                        </h1>
                    </Link>
                </div>
                {/* icon container */}
                <div className="navbar-toggle-icon">
                    <button onClick={() => {
                        if (toggle === false) {
                            toggleNav(true)
                        } else {
                            toggleNav(false)
                        }

                    }}
                        disabled={animate ? false : true} // prevent button toggle spamming
                    >
                        {/* conditional icon rendering */}
                        {
                            toggle ?
                                // close icon
                                <svg width="47" height="23" viewBox="0 0 47 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 5L35 18M8 18L35 5" stroke="#F2EEE8" stroke-width="2" stroke-linecap="round" />
                                </svg> :
                                // toggle icon
                                <svg
                                    width="29" height="14" viewBox="0 0 24 9" fill="none" xmlns="http://www.w3.org/2000/svg" >
                                    <path d="M0.903992 1.07999H23.0707M0.903992 8.37036H23.0707" stroke="#FBCF82" strokeLinecap="round" />
                                </svg>
                        }
                    </button>
                </div>
            </nav>
            <section className='menu-wrapper' ref={wrapper} hidden={toggle ? false : true}>
                {/* Menu options (links) */}
                <div className="menu-option-container">
                    <ul className="menu-options" ref={links}>
                        <motion.li
                            className="menu-links home"
                            whileHover={{ x: 10, scaleX: 1.1 }}
                            transition={{ type: 'tween', duration: 0.2, delay: 0.1, ease: 'easeOut' }}
                        >
                            <Link to="/about">ABOUT</Link>
                        </motion.li>
                        <motion.li
                            className="menu-links projects"
                            whileHover={{ x: 10, scaleX: 1.1 }}
                            transition={{ type: 'tween', duration: 0.2, delay: 0.1, ease: 'easeOut' }}
                        >
                            <Link to="/contact">Contact</Link>
                        </motion.li>
                        <motion.li
                            className="menu-links about"
                            whileHover={{ x: 10, scaleX: 1.1 }}
                            transition={{ type: 'tween', duration: 0.2, delay: 0.1, ease: 'easeOut' }}
                        >
                            <Link to="/project">Works</Link>
                        </motion.li>
                    </ul>
                </div>
                <div className="menu-footer" ref={footer}>
                    <div className="socials">
                        <ul>
                            <li>
                                <a href="https://www.instagram.com/clayzz1.0">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M20.9471 8.30496C20.937 7.54758 20.7952 6.79773 20.5281 6.08896C20.2964 5.49111 19.9426 4.94816 19.4892 4.49479C19.0359 4.04142 18.4929 3.68761 17.8951 3.45596C17.1954 3.19331 16.4562 3.0513 15.7091 3.03596C14.7471 2.99296 14.4421 2.98096 12.0001 2.98096C9.55807 2.98096 9.24507 2.98096 8.29007 3.03596C7.54323 3.05141 6.80442 3.19343 6.10507 3.45596C5.50713 3.68745 4.96409 4.0412 4.5107 4.49459C4.05732 4.94798 3.70356 5.49102 3.47207 6.08896C3.2089 6.78809 3.06719 7.52707 3.05307 8.27396C3.01007 9.23696 2.99707 9.54196 2.99707 11.984C2.99707 14.426 2.99707 14.738 3.05307 15.694C3.06807 16.442 3.20907 17.18 3.47207 17.881C3.70395 18.4787 4.05797 19.0215 4.51151 19.4747C4.96505 19.9279 5.50813 20.2815 6.10607 20.513C6.8035 20.7862 7.54244 20.9383 8.29107 20.963C9.25407 21.006 9.55907 21.019 12.0011 21.019C14.4431 21.019 14.7561 21.019 15.7111 20.963C16.4582 20.9482 17.1974 20.8066 17.8971 20.544C18.4948 20.312 19.0376 19.9581 19.4909 19.5048C19.9442 19.0515 20.2982 18.5086 20.5301 17.911C20.7931 17.211 20.9341 16.473 20.9491 15.724C20.9921 14.762 21.0051 14.457 21.0051 12.014C21.0031 9.57196 21.0031 9.26196 20.9471 8.30496V8.30496ZM11.9941 16.602C9.44007 16.602 7.37107 14.533 7.37107 11.979C7.37107 9.42496 9.44007 7.35596 11.9941 7.35596C13.2202 7.35596 14.396 7.84302 15.263 8.71C16.13 9.57698 16.6171 10.7529 16.6171 11.979C16.6171 13.2051 16.13 14.3809 15.263 15.2479C14.396 16.1149 13.2202 16.602 11.9941 16.602V16.602ZM16.8011 8.26296C16.6595 8.26309 16.5192 8.2353 16.3884 8.18117C16.2575 8.12704 16.1386 8.04764 16.0385 7.94751C15.9384 7.84738 15.859 7.72849 15.8049 7.59765C15.7507 7.4668 15.7229 7.32656 15.7231 7.18496C15.7231 7.04346 15.7509 6.90334 15.8051 6.77262C15.8592 6.64189 15.9386 6.5231 16.0387 6.42305C16.1387 6.32299 16.2575 6.24363 16.3882 6.18948C16.519 6.13533 16.6591 6.10746 16.8006 6.10746C16.9421 6.10746 17.0822 6.13533 17.2129 6.18948C17.3436 6.24363 17.4624 6.32299 17.5625 6.42305C17.6625 6.5231 17.7419 6.64189 17.7961 6.77262C17.8502 6.90334 17.8781 7.04346 17.8781 7.18496C17.8781 7.78096 17.3961 8.26296 16.8011 8.26296Z" fill="#F2EEE8" />
                                        <path d="M11.994 14.982C13.6525 14.982 14.997 13.6375 14.997 11.979C14.997 10.3204 13.6525 8.97595 11.994 8.97595C10.3355 8.97595 8.99097 10.3204 8.99097 11.979C8.99097 13.6375 10.3355 14.982 11.994 14.982Z" fill="#F2EEE8" />
                                    </svg>
                                </a>
                            </li>
                            <li>
                                <a href="https://github.com/NoxturneDev">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g clip-path="url(#clip0_267_9)">
                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M12 0C5.37 0 0 5.37 0 12C0 17.31 3.435 21.795 8.205 23.385C8.805 23.49 9.03 23.13 9.03 22.815C9.03 22.53 9.015 21.585 9.015 20.58C6 21.135 5.22 19.845 4.98 19.17C4.845 18.825 4.26 17.76 3.75 17.475C3.33 17.25 2.73 16.695 3.735 16.68C4.68 16.665 5.355 17.55 5.58 17.91C6.66 19.725 8.385 19.215 9.075 18.9C9.18 18.12 9.495 17.595 9.84 17.295C7.17 16.995 4.38 15.96 4.38 11.37C4.38 10.065 4.845 8.985 5.61 8.145C5.49 7.845 5.07 6.615 5.73 4.965C5.73 4.965 6.735 4.65 9.03 6.195C9.99 5.925 11.01 5.79 12.03 5.79C13.05 5.79 14.07 5.925 15.03 6.195C17.325 4.635 18.33 4.965 18.33 4.965C18.99 6.615 18.57 7.845 18.45 8.145C19.215 8.985 19.68 10.05 19.68 11.37C19.68 15.975 16.875 16.995 14.205 17.295C14.64 17.67 15.015 18.39 15.015 19.515C15.015 21.12 15 22.41 15 22.815C15 23.13 15.225 23.505 15.825 23.385C18.2072 22.5807 20.2772 21.0497 21.7437 19.0074C23.2101 16.965 23.9993 14.5143 24 12C24 5.37 18.63 0 12 0Z" fill="#F2EEE8" />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_267_9">
                                                <rect width="24" height="24" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </a>
                            </li>
                            <li>
                                <a href="https://www.linkedin.com/in/galih-adhi-kusuma-693368240/">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.429 8.969H13.143V10.819C13.678 9.755 15.05 8.799 17.111 8.799C21.062 8.799 22 10.917 22 14.803V22H18V15.688C18 13.475 17.465 12.227 16.103 12.227C14.214 12.227 13.429 13.572 13.429 15.687V22H9.429V8.969V8.969ZM2.57 21.83H6.57V8.799H2.57V21.83V21.83ZM7.143 4.55C7.14315 4.88528 7.07666 5.21724 6.94739 5.52659C6.81812 5.83594 6.62865 6.11651 6.39 6.352C5.9064 6.83262 5.25181 7.10165 4.57 7.1C3.88939 7.09954 3.23631 6.8312 2.752 6.353C2.51421 6.11671 2.32539 5.83582 2.19634 5.52643C2.0673 5.21704 2.00058 4.88522 2 4.55C2 3.873 2.27 3.225 2.753 2.747C3.23689 2.26816 3.89024 1.9997 4.571 2C5.253 2 5.907 2.269 6.39 2.747C6.872 3.225 7.143 3.873 7.143 4.55Z" fill="#F2EEE8" />
                                    </svg>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>
        </>
    )
}

export default Menu